/**
 * "Print this board" as a one-way request (parity card **P-090**).
 *
 * The board `···` menu lives in `BoardHeader`, which is mounted **outside** the table view's
 * provider (`routes/screens.tsx` renders the header, then the tabs, then whichever view the
 * tab resolves to). So the menu cannot reach `printBoard()` on the table store, and hoisting
 * the store above the header would make every non-table view carry a table.
 *
 * The request therefore travels the way `openColumnSettings` already does — a DOM
 * `CustomEvent` on `window`, dispatched by the menu and answered by whichever view is mounted.
 * `TableView` answers it by unvirtualising the grid first; when nothing answers (a kanban or a
 * dashboard tab, or the board still loading) the dispatcher falls back to a plain
 * `window.print()`, so the row is never a dead end.
 */
export const BOARD_PRINT_EVENT = 'elizmonday:print-board';

/**
 * Asks the mounted view to print. Returns after the listener has *acknowledged*, not after the
 * print dialog closes: a listener acknowledges synchronously by calling `event.preventDefault()`
 * (the event is cancelable for exactly this), and with no acknowledgement this prints directly.
 */
export function requestBoardPrint(): void {
  if (typeof window === 'undefined') return;
  const event = new CustomEvent(BOARD_PRINT_EVENT, { cancelable: true });
  const handled = !window.dispatchEvent(event);
  if (!handled) window.print();
}

/** Subscribes to the request; returns the unsubscribe. The listener must acknowledge. */
export function onBoardPrintRequested(listener: () => void): () => void {
  const handler = (event: Event) => {
    event.preventDefault();
    listener();
  };
  window.addEventListener(BOARD_PRINT_EVENT, handler);
  return () => window.removeEventListener(BOARD_PRINT_EVENT, handler);
}
